import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "common/hooks";

import {
  onboardingV2AnswersAtom,
  onboardingV2ModalRequestedAtom,
  onboardingV2PendingNewUserAtom,
} from "./atoms";
import {
  hasOnboardingAnswers,
  useClearOnboardingForSession,
} from "./useResetOnboarding";

/**
 * Records that the account that just signed in has never logged in before, so
 * the onboarding flow should run for it.
 *
 * Called from the auth success sites that can see the server's `firstLogin` —
 * password login and social login — because only the server can tell us an
 * account is new.
 *
 * **Carry-over.** Unanswered onboarding is not the same as answered-by-a-guest.
 * If there are answers sitting in this browser, the guest who gave them minutes
 * ago is the person now signing in: the account inherits them and the flow does
 * not run a second time. After a log out there are none — they leave with the
 * session — so the next person to sign up here gets the flow properly, even
 * though the browser's completion flag says onboarding has run here before.
 */
export function useMarkNewUserForOnboarding() {
  const answers = useAtomValue(onboardingV2AnswersAtom);
  const setPending = useSetAtom(onboardingV2PendingNewUserAtom);

  return useCallback(
    (isNewAccount: boolean) => {
      if (!isNewAccount) return;
      if (hasOnboardingAnswers(answers)) return;
      setPending(true);
    },
    [answers, setPending],
  );
}

/**
 * Watches the session: a sign-in brings the flow up as the full modal, a log
 * out clears the departing user's onboarding answers.
 *
 * **Sign-in.** Any settled transition from signed out to signed in — password,
 * social, or a social sign-up that logs the account in before its profile
 * steps — asks for the modal. The modal only appears if the flow is still owed
 * (not finished here, or a new account), and not until the auth modal has
 * closed; @see `onboardingV2PresentationAtom`. Someone who is already signed in
 * when the page loads is not a transition, so they get the docked sheet.
 *
 * Mounted once, by `OnboardingFlow`. Watching the auth state rather than
 * patching each `logout()` call site — there are four, across the menu, the
 * profile forms and a toast handler — so a fifth added later is covered without
 * anyone remembering to.
 *
 * `isLoading` is what makes this safe. `useAuth` hydrates late: `user` is
 * undefined on the first render and again after every invalidation, and
 * treating either as a log out would wipe a signed-in user's answers mid-session.
 * Nothing is read until auth has settled.
 *
 * An account *switch* clears too. The answers belonged to whoever left.
 */
export function useOnboardingSessionHost() {
  const { user, isLoading } = useAuth();
  const clearForSession = useClearOnboardingForSession();
  const setModalRequested = useSetAtom(onboardingV2ModalRequestedAtom);

  // The last settled identity, so a transition can be told from a re-render.
  // `undefined` means "nothing settled yet", which is not the same as signed out.
  const lastUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isLoading) return;

    const rawId = (user as { id?: string | number } | undefined)?.id;
    const userId = rawId == null ? "" : String(rawId);
    const previous = lastUserIdRef.current;
    lastUserIdRef.current = userId;

    // Nothing settled before this, so there is no transition to act on — this
    // is a first load, signed in or not.
    if (previous === undefined) return;
    if (previous === userId) return;
    // Signing *in* carries the guest's answers over; only leaving clears them.
    if (previous === "") {
      setModalRequested(true);
      return;
    }

    clearForSession();
    // An account switch is a sign-in too.
    if (userId !== "") setModalRequested(true);
  }, [clearForSession, isLoading, setModalRequested, user]);
}
